/**
 * useEventTypeConfig Hook
 * Event type loading and configuration
 */

import { useState, useEffect, useCallback } from 'react';
import { ReadonlyURLSearchParams } from 'next/navigation';

export interface EventTypeConfig {
  name: string | null;
  description: string | null;
  groupEmails: string[];
  availableDurations: number[];
  location: string;
  isOnline: boolean;
  defaultDuration: number;
}

export function useEventTypeConfig(searchParams: ReadonlyURLSearchParams | null) {
  const [eventType, setEventType] = useState<string | null>(null);
  const [config, setConfig] = useState<EventTypeConfig>({
    name: null,
    description: null,
    groupEmails: [],
    availableDurations: [],
    location: '',
    isOnline: true,
    defaultDuration: 30,
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const eventTypeParam = searchParams?.get('eventType');

    if (eventTypeParam) {
      setEventType(eventTypeParam);
      setIsLoading(true);

      fetch(`/api/booking/event-types?eventType=${encodeURIComponent(eventTypeParam)}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.error) {
            console.error('Error loading event type config:', data.error);
            setIsLoading(false);
            return;
          }

          setConfig({
            name: data.name || null,
            description: data.description || null,
            groupEmails: data.groupEmails || [],
            availableDurations: data.availableDurations || [],
            location: data.location || '',
            isOnline: data.isOnline !== undefined ? data.isOnline : true,
            defaultDuration: data.defaultDuration || 30,
          });

          console.log('Event type config loaded:', {
            eventType: eventTypeParam,
            groupEmails: data.groupEmails,
            name: data.name,
          });

          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching event type config:', error);
          setIsLoading(false);
        });
    } else {
      setEventType(null);
      setConfig({
        name: null,
        description: null,
        groupEmails: [],
        availableDurations: [],
        location: '',
        isOnline: true,
        defaultDuration: 30,
      });
      setIsLoading(false);
    }
  }, [searchParams]);

  const resetEventType = useCallback(() => {
    setEventType(null);
    setConfig({
      name: null,
      description: null,
      groupEmails: [],
      availableDurations: [],
      location: '',
      isOnline: true,
      defaultDuration: 30,
    });
  }, []);

  return {
    eventType,
    config,
    isLoading,
    resetEventType,
  };
}
